import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SsoAuthService } from '../../sso-auth.service';
import { CommonModule } from '@angular/common';
import { PhilDropdownComponent } from '../../phil-dropdown/phil-dropdown.component';
import { ToastrService } from 'ngx-toastr';
import { SplashScreen } from '../splash-screen/splash-screen';
import { catchError, forkJoin, of, timer } from 'rxjs';
import { environment } from '../../../environments/environment.development';

// state for maintaining the login process
interface IState {
  members: any[];
  memberOptions: any[];
  selectedMemId: string;
  selectedMember: any;
  showOtp: boolean;
  otp: string;
  countdown: number;
  otpRequestId: string;
  otpError: string;
  fcError: string;
  loginCount: number;
  selectedContact: string;
  mobileNumbers: string[];
  emails: string[];
}

// initial state when everything is null
const initialState: IState = {
  members: [],
  memberOptions: [],
  selectedMemId: '',
  selectedMember: null,
  showOtp: false,
  otp: '',
  countdown: 60,
  otpRequestId: '',
  otpError: '',
  fcError: '',
  loginCount: 0,
  selectedContact: '',
  mobileNumbers: [],
  emails: [],
};
@Component({
  selector: 'app-sso-login',
  templateUrl: './sso-login.component.html',
  styleUrls: ['./sso-login.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, FormsModule, PhilDropdownComponent, SplashScreen],
  standalone: true,
})
export class SsoLoginComponent implements OnInit {
  loginForm!: FormGroup;
  accounts: any;
  contactUs: any = {
    IN: {
      title: 'Facing an issue? Contact us',
      items: [
        {
          label: 'For oblation',
          contact: { value: '+919153963392', href: 'tel:+919153963392' },
          email: { value: 'oblation@satsang.org.in', href: 'mailto:oblation@satsang.org.in' },
        },
        {
          label: 'For accommodation',
          contact: { value: '+919153963396', href: 'tel:+919153963396' },
          email: {
            value: 'support@acco.satsangphilanthropy.com',
            href: 'mailto:support@acco.satsangphilanthropy.com',
          },
        },
        {
          label: 'Anything else',
          contact: { value: '18003450122', href: 'tel:18003450122' },
        },
      ],
    },
    NE: {
      contact: { value: '+977-9768615361', href: 'tel: +9779768615361' },
      email: {
        value: 'satsangnepalphilanthropy@gmail.com',
        href: 'mailto: satsangnepalphilanthropy@gmail.com',
      },
    },
  };
  selectedContactUs!: any;
  countryList: { id: string; title: string }[] = [
    {
      id: 'IN',
      title: 'India',
    },
    {
      id: 'NE',
      title: 'Nepal',
    },
    // {
    //   id: 'EU',
    //   title: 'Europe',
    // },
  ];
  selectedAccount = 'Family Code';
  state!: IState;
  selectedCountry = new FormControl(this.countryList[0].id);
  allowedKeys: any[] = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];
  isLoading = false;
  isLoadingSplash = true;
  returnUrl = '';
  openSecurityForm = false;
  securityForm!: FormGroup;
  isIdentityPresentInSec = false;
  securityStep = 1;
  members: any[] = [];
  showMemberCard = false;
  selectedMember: any = null;
  otpSentTo: string = '';
  appMode: string = '';
  isRedirected: boolean = false;
  appCountryName: string = '';
  appCountryId: string = '';
  passwordAllowedCountry: string[] = environment.allowedPassword;
  isPasswordVisible: boolean = false;
  // selectContact(contact: string, event: Event) {
  //   this.selectedContact = contact;
  //   event.stopPropagation();
  // }

  // [DONE]
  constructor(
    private fb: FormBuilder,
    private authService: SsoAuthService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    this.selectedCountry.setValue(this.countryList[0].id);
    const rules: any = environment.loginRule;
    this.accounts = rules[this.countryList[0].id];
    console.log(this.accounts);
    // login form for 3 different login types
    this.loginForm = this.fb.group({
      familyCode: [''],
      password: [''],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      slno: [''],
      email: ['', [Validators.required, Validators.email]],
      phoneOrEmail: [''],
    });
    this.state = { ...initialState };

    this.loginForm.get('phoneOrEmail')?.valueChanges.subscribe((val) => {
      if (this.isValidEmail(val)) {
        this.loginForm.get('email')?.setValue(val);
      } else if (this.isValidPhone(val)) {
        this.loginForm.get('phone')?.setValue(val);
      } else {
        this.loginForm.get('email')?.setValue('');
        this.loginForm.get('phone')?.setValue('');
      }
    });

    // when ever anything in the login form changes reset the enter state to initial state
    // as it marks the beginning of the login process
    this.loginForm.get('phone')?.valueChanges.subscribe((val) => {
      if (this.selectedAccount == 'Phone Number') {
        this.state = { ...initialState };
      }
      this.state.selectedContact = this.isValidPhone(val) ? this.maskContact(val) : '';
    });

    this.loginForm.get('email')?.valueChanges.subscribe((val) => {
      this.state.selectedContact = this.isValidEmail(val) ? this.maskEmail(val) : '';
    });

    this.loginForm.get('familyCode')?.valueChanges.subscribe((val) => {
      if (this.selectedAccount == 'Family Code') {
        this.state = { ...initialState };
        this.loginForm.get('email')?.reset();
        this.loginForm.get('phone')?.reset();
        this.loginForm.get('phoneOrEmail')?.reset();
      }
    });

    this.loginForm.get('slno')?.valueChanges.subscribe((val) => {
      if (this.selectedAccount == 'Sl. Number') {
        this.state = { ...initialState };
        this.loginForm.get('email')?.reset();
        this.loginForm.get('phone')?.reset();
        this.loginForm.get('phoneOrEmail')?.reset();
      }
    });

    // initialize the security question form
    this.securityForm = this.fb.group({
      fcSecurityForm: ['', [Validators.required, Validators.minLength(12)]],
      nameAsPerAP: ['', [Validators.required]],
      ritwikName: ['', [Validators.required]],
      dpSerialNo: [''],
      arghyaSerialNo: [''],
      identityNo: [''],
      recentApSerialNo: [''],
      recentApAmount: [''],
    });
  }

  // select a login type
  selectAccount(account: string) {
    if (account === this.selectedAccount) return;
    if (!this.accounts.accounts.includes(account)) return;
    console.log('****');
    this.state = { ...initialState };
    this.selectedAccount = account;
    this.loginForm.reset();
    console.log(this.state);
    this.cdr.detectChanges();
  }

  // validate the fc in form [DONE]
  validateFamilyCode() {
    const value = this.loginForm.get('familyCode')?.value || '';

    if (!value) {
      this.state.fcError = 'Family code is required';
      return false;
    } else if (value.length !== 12) {
      this.state.fcError = 'Family code must be exactly 12 digits';
      return false;
    } else {
      this.state.fcError = '';
      return true;
    }
  }

  // clear the query params [DONE]
  clearQueryParams(): void {
    this.router.navigate([], {
      queryParams: {},
      replaceUrl: true,
    });
  }

  // on init
  ngOnInit(): void {
    // whenever I get the appId and code challenge call the authorize function to check if the current SSO session is valid
    // if valid it returns the app specific codes so that the app can login itself
    // else show the login page
    this.route.queryParams.subscribe((params) => {
      const appId = params['appId'];
      const codeChallenge = params['codeChallenge'];
      const isLogout = params['logout'];
      this.appMode = params['appMode'];
      this.returnUrl = params['returnUrl'] || null;
      this.selectedContactUs = this.contactUs['IN'];
      if (appId) {
        console.log('APP ID: ', appId);
        this.getCountryByAppId(appId);
        this.isRedirected = true;
      }

      // if route changes and none of appId, codeChallenge or logout in query params.
      // See if session exists else logout
      if (!appId && !codeChallenge && !isLogout) {
        const existingSession = localStorage.getItem('sso-session');
        if (existingSession) {
          this.router.navigate(['/dashboard']);
          return;
        }
      }

      // handle the logout case
      if (isLogout) {
        this.isLoadingSplash = true;
        forkJoin([this.authService.logOut(), timer(1000)]).subscribe({
          next: ([res, timer]) => {
            if (!res.error) {
              localStorage.clear();
              sessionStorage.clear();
              this.toastr.success('Successfully logged out');
              this.isLoadingSplash = false;
              if (this.returnUrl) window.location.href = this.returnUrl;
            } else {
              this.router.navigate(['/dashboard']);
              this.isLoadingSplash = false;
            }
            this.clearQueryParams();
          },
          error: (err) => {
            console.error('Authorize Session Error:', err);
            this.toastr.success(err.message || 'Unable to logout');
            this.clearQueryParams();
            this.router.navigate(['/dashboard']);
            this.isLoadingSplash = false;
          },
        });
      }
      // handle creating access code and redirect to respective app
      else {
        console.log(appId, codeChallenge);
        if (appId && appId !== 'null' && appId !== 'undefined') {
          sessionStorage.setItem('sso_appId', appId);
        }

        if (codeChallenge && codeChallenge !== 'null' && codeChallenge !== 'undefined') {
          sessionStorage.setItem('sso_codeChallenge', codeChallenge);
        }

        const payload = {
          appId: appId ? Number(appId) : null,
          codeChallenge: codeChallenge || null,
        };

        this.isLoadingSplash = true;
        forkJoin([
          this.authService.authorizeSession(payload).pipe(
            catchError((error) => {
              return of(null);
            }),
          ),
          timer(1000),
        ]).subscribe({
          next: ([res, timer]) => {
            if (res?.data?.redirectUrl && res?.data?.accessCode) {
              console.log('Authorize Session Response:', res);
              let redirectUrl = ``;
              if (this.returnUrl && !environment.production) {
                // if in test and environment file has local redirect url, then redirect to local
                redirectUrl = `${this.returnUrl}?accessCode=${res.data.accessCode}`;
              } else {
                // else redirect to url coming from backend
                if (this.appMode === 'true') {
                  redirectUrl = `myapp://sso/callback?accessCode=${res.data.accessCode}`;
                } else {
                  redirectUrl = `${res.data.redirectUrl}?accessCode=${res.data.accessCode}`;
                }
              }
              sessionStorage.removeItem('sso_appId');
              sessionStorage.removeItem('sso_codeChallenge');
              window.location.href = redirectUrl;
            } else if (res !== null) {
              this.router.navigate(['/dashboard']);
              // sessionStorage.removeItem('sso_appId');
              // sessionStorage.removeItem('sso_codeChallenge');
            } else {
              // sessionStorage.removeItem('sso_appId');
              // sessionStorage.removeItem('sso_codeChallenge');
              this.isLoadingSplash = false;
            }
          },
          error: (err) => {
            console.error('Authorize Session Error:', err);
            this.router.navigate(['/dashboard']);
            sessionStorage.removeItem('sso_appId');
            sessionStorage.removeItem('sso_codeChallenge');
            this.isLoadingSplash = false;
          },
        });
      }
    });
  }

  togglePassword(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  // for member card selection
  selectMember(member: any) {
    this.selectedMember = member;
    this.state.selectedMemId = member.memuuid;
  }

  // create session after selecting a member
  continueWithMember() {
    if (!this.selectedMember) return;
    sessionStorage.setItem('selectedMemId', this.state.selectedMemId);

    const appId = sessionStorage.getItem('sso_appId');
    const codeChallenge = sessionStorage.getItem('sso_codeChallenge');

    const payload: any = {
      userId: this.state.selectedMemId,
    };

    if (appId && appId !== 'null' && appId !== 'undefined') {
      payload.appId = Number(appId);
    }

    if (codeChallenge && codeChallenge !== 'null' && codeChallenge !== 'undefined') {
      payload.codeChallenge = codeChallenge;
    }

    this.authService.createSession(payload).subscribe({
      next: (res) => {
        console.log('Session created successfully:', res);
        if (res.data?.redirectUrl && res.data?.accessCode) {
          sessionStorage.removeItem('sso_appId');
          sessionStorage.removeItem('sso_codeChallenge');
          sessionStorage.removeItem('state_token');
          localStorage.setItem('sso-session', res.data['sso-session']);
          let redirectUrl = ``;
          if (this.returnUrl && !environment.production) {
            redirectUrl = `${this.returnUrl}?accessCode=${res.data.accessCode}`;
          } else {
            // redirectUrl = `${res.data.redirectUrl}?accessCode=${res.data.accessCode}`;
            if (this.appMode === 'true') {
              redirectUrl = `myapp://sso/callback?accessCode=${res.data.accessCode}`;
            } else {
              redirectUrl = `${res.data.redirectUrl}?accessCode=${res.data.accessCode}`;
            }
          }
          window.location.href = redirectUrl;
        } else {
          localStorage.setItem('sso-session', res.data['sso-session']);
          // window.location.href = 'https://www.satsang.org.in/home';
          this.router.navigate(['/dashboard']);
          sessionStorage.removeItem('state_token');
          sessionStorage.removeItem('sso_appId');
          sessionStorage.removeItem('sso_codeChallenge');
          // this.toastr.error(res.message || 'OTP verification failed');
        }
      },
      error: (err) => {
        console.error('Error creating session:', err);
      },
    });
  }

  // allow only digits to be entered
  allowOnlyDigits(event: KeyboardEvent): void {
    const key = event.key;
    // Allow digits 0-9, and also allow special keys like Backspace, Tab, Arrow keys, etc.
    if (!/^\d$/.test(key) && !this.allowedKeys.includes(key)) {
      event.preventDefault();
    }
  }

  // on changing fc keep checking the following
  onFamilyCodeChange() {
    const familyCode = this.loginForm.get('familyCode')?.value;
    const valid = this.validateFamilyCode();
    this.state = { ...initialState };

    if (valid) {
      this.isLoading = true;
      this.authService.validateFamilyCode(familyCode, this.selectedCountry?.value!).subscribe({
        next: (res: any) => {
          if (!res.error && res.data) {
            if (res.data?.isOTPSent) {
              this.state.showOtp = true;
              this.state.otpRequestId = res.data.otpRequestId;
              this.otpSentTo = res.data.contactNo;
              this.startCountdown();
              this.toastr.success(res.message || 'OTP sent');
            }
            if (!res.data.isOTPSent) {
              this.state.mobileNumbers = res.data.contacts;
              this.state.emails = res.data.emails;
              this.toastr.success(res.message || 'More than one mobile number Detected');
            }
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.toastr.error(err?.message || 'Something went wrong');
          console.error(err);
          this.isLoading = false;
        },
      });
    }
  }

  getMemberBySn() {
    if (!this.loginForm.get('slno')?.value) {
      this.toastr.error('Please enter a valid sl number');
      return;
    }

    const mk = this.loginForm.get('slno')?.value;

    this.isLoading = true;
    this.authService.validateSerialNoCode(mk, this.selectedCountry.value!).subscribe({
      next: (res: any) => {
        if (!res.error && res.data) {
          if (res.data?.isOTPSent) {
            this.state.showOtp = true;
            this.state.otpRequestId = res.data.otpRequestId;
            this.otpSentTo = res.data.contactNo;
            this.startCountdown();
            this.toastr.success(res.message || 'OTP sent');
          }
          if (!res.data.isOTPSent) {
            this.state.mobileNumbers = res.data.contacts;
            this.state.emails = res.data.emails;
            this.toastr.success(res.message || 'More than one mobile number Detected');
          }
        }
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error(err);
        this.isLoading = false;
        this.toastr.error(err?.message || 'Failed to validate serial number');
      },
    });
  }

  // trying to send otp
  onSubmit() {
    // complete flow for fc (done)
    if (this.selectedAccount === 'Family Code') {
      if (this.passwordAllowedCountry.includes(this.selectedCountry.value!)) {
        console.log('SUBMIT CLICKED: ', this.loginForm.value);
        const fc = this.loginForm.get('familyCode')?.value;
        const password = this.loginForm.get('password')?.value;
        const payload = { fc, password, country: this.selectedCountry.value };

        // call the verify otp method
        this.verifyPassword(payload);
      } else {
        const fc = this.loginForm.get('familyCode')?.value;
        const phone = this.loginForm.get('phone')?.value;
        const email = this.loginForm.get('email')?.value;
        if (!fc || fc.length !== 12) {
          this.toastr.error('Please enter a valid 12-digit Family Code');
          return;
        }

        if (
          this.state.mobileNumbers.length + this.state.emails.length > 1 &&
          !this.isValidPhone(phone) &&
          !this.isValidEmail(email)
        ) {
          this.toastr.error('Enter one phone number/email from the above list');
          return;
        }

        if (this.state.mobileNumbers.length + this.state.emails.length > 1) {
          let payload: any = {};
          if (this.isValidPhone(phone)) {
            payload = { fc, contactNo: phone, country: this.selectedCountry?.value };
          } else {
            payload = { fc, email, country: this.selectedCountry?.value };
          }
          this.isLoading = true;
          this.authService.sendOtpFc(payload).subscribe({
            next: (res: any) => {
              if (!res.error && res.data) {
                if (res.data?.isOTPSent) {
                  this.state.showOtp = true;
                  this.startCountdown();
                  this.state.otpRequestId = res.data.otpRequestId;
                  this.otpSentTo = res.data.contactNo;
                  this.toastr.success(res.message || 'OTP sent');
                }
              }
              this.isLoading = false;
            },
            error: (err) => {
              console.error('sendOtpFc error:', err);
              this.toastr.error(err.message || 'Something Went Wrong!');
              this.isLoading = false;
            },
          });
        } else {
          this.onFamilyCodeChange();
        }
      }
    }

    // complete flow for sN
    if (this.selectedAccount === 'Sl. Number') {
      if (this.state.mobileNumbers.length + this.state.emails.length === 0) {
        this.getMemberBySn();
        return;
      }

      if (!this.loginForm.get('slno')?.value) {
        this.toastr.error('Please enter a valid serial number');
        return;
      }
      const phoneNo = this.loginForm.get('phone')?.value;
      const email = this.loginForm.get('email')?.value;
      const slNo = this.loginForm.get('slno')?.value;
      if (!this.isValidPhone(phoneNo) && !this.isValidEmail(email)) {
        this.toastr.error('Please enter a valid phone number/email');
        return;
      }

      let payload: any = {};
      if (this.isValidPhone(phoneNo)) {
        payload = {
          serialNo: Number(slNo),
          contactNo: phoneNo,
          country: this.selectedCountry?.value,
        };
      } else {
        payload = { serialNo: Number(slNo), email, country: this.selectedCountry?.value };
      }

      this.isLoading = true;
      this.authService.sendOtpSlno(payload).subscribe({
        next: (res: any) => {
          if (res.data.isOTPSent) {
            this.state.showOtp = true;
            this.state.otpRequestId = res.data.otpRequestId;
            this.otpSentTo = res.data.contactNo;
            this.startCountdown();
            this.toastr.success('OTP sent successfully');
          }
          this.isLoading = false;
        },
        error: (err) => {
          console.error(err);
          this.isLoading = false;
          this.toastr.error(err?.message || 'Failed to send OTP. Please try again.');
        },
      });
    }

    // phone number
    if (this.selectedAccount === 'Phone Number') {
      if (!this.loginForm.get('phone')?.value || !this.loginForm.get('phone')?.valid) {
        this.toastr.error('Please enter a valid phone number');
        return;
      }

      const phoneNo = this.loginForm.get('phone')?.value;
      const memId = this.state.selectedMemId;

      let payload: any = { contactNo: phoneNo, country: this.selectedCountry?.value };
      if (memId) {
        payload.id = memId;
      }

      this.isLoading = true;
      this.authService.sendOtp(payload).subscribe({
        next: (res: any) => {
          const isOTPSent = res.data.isOTPSent;
          if (isOTPSent) {
            this.state.showOtp = true;
            this.state.otpRequestId = res.data.otpRequestId;
            this.otpSentTo = res.data.contactNo;
            this.startCountdown();
            this.toastr.success('OTP sent successfully');
          } else {
            this.state.members = res.data.accounts;
            if (!this.state.members?.length) {
              this.toastr.error('No members found with this phone number');
            }

            this.state.memberOptions = this.state.members.map((mem) => {
              return {
                id: mem.id,
                title: mem.fullName,
              };
            });
          }
          this.isLoading = false;
        },
        error: (err) => {
          console.error(err);
          this.isLoading = false;
          this.toastr.error(err?.message || 'Failed to send OTP. Please try again.');
        },
      });

      return;
    }

    if (this.selectedAccount === 'Email') {
      if (!this.loginForm.get('email')?.value || !this.loginForm.get('email')?.valid) {
        this.toastr.error('Please enter a valid email');
        return;
      }

      const email = this.loginForm.get('email')?.value;

      let payload: any = { email };

      this.isLoading = true;
      this.authService.signinEmail(payload).subscribe({
        next: (res: any) => {
          const isOTPSent = res.data.isOTPSent;
          if (isOTPSent) {
            this.state.showOtp = true;
            this.state.otpRequestId = res.data.otpRequestId;
            this.otpSentTo = res.data.contactNo;
            this.startCountdown();
            this.toastr.success('OTP sent successfully');
          }
          this.isLoading = false;
        },
        error: (err) => {
          console.error(err);
          this.isLoading = false;
          this.toastr.error(err?.message || 'Failed to send OTP. Please try again.');
        },
      });

      return;
    }
  }

  getFormControlByname(name: string) {
    return this.loginForm.get(name) as FormControl;
  }

  // restrict FC Input from wrong chars
  restrictFCInput(event: KeyboardEvent): void {
    const input = event.key;
    const limit = event.target as HTMLInputElement;
    const pattern = /[0-9]/;

    if (this.allowedKeys.includes(input) || event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }
    if (!pattern.test(input) || limit.value.length > 11) {
      event.preventDefault(); // Prevent input if it doesn't match the pattern
    }
  }

  // reestrict AP Serial from wrong chars
  restrictAPSerialInput(event: KeyboardEvent): void {
    const input = event.key;
    const limit = event.target as HTMLInputElement;
    const pattern = /[0-9]/;

    if (this.allowedKeys.includes(input) || event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }
    if (!pattern.test(input) || limit.value.length > 17) {
      event.preventDefault(); // Prevent input if it doesn't match the pattern
    }
  }

  // start countdown
  startCountdown() {
    this.state.countdown = 60;
    const interval = setInterval(() => {
      if (this.state.countdown > 0 && this.state.showOtp === true) {
        this.state.countdown--;
      } else {
        clearInterval(interval);
      }
    }, 1000);
  }

  // verify the otp entered
  verifyOtp() {
    // const appId = sessionStorage.getItem('sso_appId');
    // const codeChallenge = sessionStorage.getItem('sso_codeChallenge');

    if (!this.state.otpRequestId || !this.validateOtp()) return;

    const payload: any = {
      id: parseInt(this.state.otpRequestId),
      otp: this.state.otp,
    };

    const appId = sessionStorage.getItem('sso_appId');
    const codeChallenge = sessionStorage.getItem('sso_codeChallenge');

    if (appId && appId !== 'null' && appId !== 'undefined') {
      payload.appId = Number(appId);
    }

    if (codeChallenge && codeChallenge !== 'null' && codeChallenge !== 'undefined') {
      payload.codeChallenge = codeChallenge;
    }

    // call the verify otp method
    this.isLoading = true;
    this.authService.verifyOtp(payload).subscribe({
      next: (res: any) => {
        console.log(res);
        this.isLoading = false;
        this.toastr.success(res.message || 'OTP sent');

        if (res?.data?.stateToken) {
          sessionStorage.setItem('state_token', res.data.stateToken);
        }

        if (res?.data?.showSecurityQuestions) {
          this.openSecurityForm = true;
          return;
        }

        if (res.data?.members && res.data.members.length > 1) {
          this.members = res.data.members;
          this.showMemberCard = true;
        } else {
          if (res.data['sso-session']) {
            // this.selectMember(res.data?.members[0])
            // this.continueWithMember();

            if (res.data?.redirectUrl && res.data?.accessCode) {
              // this.toastr.success(res.message || 'OTP verified successfully', 'Success');
              sessionStorage.removeItem('sso_appId');
              sessionStorage.removeItem('sso_codeChallenge');
              localStorage.setItem('sso-session', res.data['sso-session']);
              let redirectUrl = ``;
              if (this.returnUrl && !environment.production) {
                redirectUrl = `${this.returnUrl}?accessCode=${res.data.accessCode}`;
              } else {
                // redirectUrl = `${res.data.redirectUrl}?accessCode=${res.data.accessCode}`;
                if (this.appMode === 'true') {
                  redirectUrl = `myapp://sso/callback?accessCode=${res.data.accessCode}`;
                } else {
                  redirectUrl = `${res.data.redirectUrl}?accessCode=${res.data.accessCode}`;
                }
              }
              // const redirectUrl = `http://localhost:4200/signin-load?accessCode=${res.data.accessCode}`;
              window.location.href = redirectUrl;
            } else {
              localStorage.setItem('sso-session', res.data['sso-session']);
              // window.location.href = 'https://www.satsang.org.in/home';
              this.router.navigate(['/dashboard']);
              sessionStorage.removeItem('sso_appId');
              sessionStorage.removeItem('sso_codeChallenge');
              // this.toastr.error(res.message || 'OTP verification failed');
            }
          }
        }

        // if (res.data?.redirectUrl && res.data?.accessCode) {
        //   // this.toastr.success(res.message || 'OTP verified successfully', 'Success');
        //   sessionStorage.removeItem('sso_appId');
        //   sessionStorage.removeItem('sso_codeChallenge');
        //   sessionStorage.setItem('sso-session', res.data['sso-session']);
        //   let redirectUrl = ``;
        //   if (this.returnUrl && !environment.production) {
        //     redirectUrl = `${this.returnUrl}?accessCode=${res.data.accessCode}`;
        //   }
        //   else {
        //     redirectUrl = `${res.data.redirectUrl}?accessCode=${res.data.accessCode}`;
        //   }
        //   // const redirectUrl = `http://localhost:4200/signin-load?accessCode=${res.data.accessCode}`;
        //   window.location.href = redirectUrl;
        // } else {
        //   sessionStorage.setItem('sso-session', res.data['sso-session']);
        //   // window.location.href = 'https://www.satsang.org.in/home';
        //   this.router.navigate(['/dashboard'])
        //   // this.toastr.error(res.message || 'OTP verification failed');
        // }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error verifying OTP:', err);
        const errorMsg = err?.message || 'Something went wrong. Please try again.';
        this.toastr.error(errorMsg);
        this.isLoading = false;
      },
    });
  }

  verifyPassword(payload: any) {
    this.isLoading = true;
    this.openSecurityForm = false;
    console.log('STATE BEFORE VERIFY PASSWORD: ', this.state);
    const appId = sessionStorage.getItem('sso_appId');
    const codeChallenge = sessionStorage.getItem('sso_codeChallenge');

    if (appId && appId !== 'null' && appId !== 'undefined') {
      payload.appId = Number(appId);
    }

    if (codeChallenge && codeChallenge !== 'null' && codeChallenge !== 'undefined') {
      payload.codeChallenge = codeChallenge;
    }
    this.authService.verifyPassword(payload).subscribe({
      next: (res: any) => {
        console.log(res);
        this.isLoading = false;
        if (!res.error) {
          this.toastr.success(res.message || 'Authnticated!');

          if (res?.data?.stateToken) {
            sessionStorage.setItem('state_token', res.data.stateToken);
          }

          if (res?.data?.showSecurityQuestions) {
            this.openSecurityForm = true;
            return;
          }

          if (res.data?.members && res.data.members.length > 1) {
            this.members = res.data.members;
            this.state.showOtp = true;
            this.showMemberCard = true;
          } else {
            if (res.data['sso-session']) {
              if (res.data?.redirectUrl && res.data?.accessCode) {
                sessionStorage.removeItem('sso_appId');
                sessionStorage.removeItem('sso_codeChallenge');
                localStorage.setItem('sso-session', res.data['sso-session']);
                let redirectUrl = ``;
                if (this.returnUrl && !environment.production) {
                  redirectUrl = `${this.returnUrl}?accessCode=${res.data.accessCode}`;
                } else {
                  if (this.appMode === 'true') {
                    redirectUrl = `myapp://sso/callback?accessCode=${res.data.accessCode}`;
                  } else {
                    redirectUrl = `${res.data.redirectUrl}?accessCode=${res.data.accessCode}`;
                  }
                }
                window.location.href = redirectUrl;
              } else {
                localStorage.setItem('sso-session', res.data['sso-session']);
                this.router.navigate(['/dashboard']);
                sessionStorage.removeItem('sso_appId');
                sessionStorage.removeItem('sso_codeChallenge');
              }
            }
          }
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error('Error verifying OTP:', err);
        const errorMsg = err?.message || 'Something went wrong. Please try again.';
        this.toastr.error(errorMsg);
        this.isLoading = false;
      },
    });
  }

  // validate the otp entered
  validateOtp() {
    if (!this.state.otp) {
      this.state.otpError = 'OTP is required';
      return false;
    } else if (!/^\d+$/.test(this.state.otp)) {
      this.state.otpError = 'OTP must contain only numbers';
      return false;
    } else if (this.state.otp.length !== 6) {
      this.state.otpError = 'OTP must be exactly 6 digits';
      return false;
    } else {
      this.state.otpError = '';
      return true;
    }
  }

  // done
  onSelectMem(selected: any) {
    console.log('On select', selected);
    this.state.selectedMemId = selected;
    this.state.selectedMember = this.state.members.find((mem) => mem.id === selected);
    console.log(this.state.selectedMember, '&&&&&');
    this.cdr.detectChanges();
  }

  // get the button text
  isButtonDisabled(): { text: string; isDisabled: boolean } {
    if (this.selectedAccount === 'Family Code') {
      if (this.passwordAllowedCountry.includes(this.selectedCountry.value!)) {
        if (!this.loginForm.get('familyCode')?.value || !this.loginForm.get('password')?.value)
          return { text: 'Enter credentials', isDisabled: true };
        else return { text: 'Login', isDisabled: false };
      } else {
        if (
          !this.loginForm.get('familyCode')?.value ||
          this.loginForm.get('familyCode')?.value.length !== 12
        )
          return { text: 'Enter FC', isDisabled: true };
        else if (this.state.mobileNumbers.length + this.state.emails.length === 1)
          return { text: 'Login', isDisabled: false };
        else if (
          !this.isValidPhone(this.loginForm.get('phone')?.value) &&
          !this.isValidEmail(this.loginForm.get('email')?.value) &&
          this.state.mobileNumbers.length + this.state.emails.length > 1
        )
          return { text: 'Enter phone number/email', isDisabled: true };
        else return { text: 'Login', isDisabled: false };
      }
    } else if (this.selectedAccount == 'Phone Number') {
      if (!this.loginForm.get('phone')?.valid)
        return { text: 'Enter phone number', isDisabled: true };
      else if (this.state.members.length && !this.state.selectedMemId)
        return { text: 'Select member', isDisabled: true };
      else return { text: 'Login', isDisabled: false };
    } else if (this.selectedAccount == 'Sl. Number') {
      if (!this.loginForm.get('slno')?.value)
        return { text: 'Enter Serial Number', isDisabled: true };
      else if (
        this.loginForm.get('slno')?.value &&
        this.state.mobileNumbers.length + this.state.emails.length === 0
      )
        return { text: 'Verify Serial Number', isDisabled: false };
      else if (
        this.loginForm.get('slno')?.value &&
        (this.isValidPhone(this.loginForm.get('phone')?.value) ||
          this.isValidEmail(this.loginForm.get('email')?.value))
      )
        return { text: 'Login', isDisabled: false };
      else if (this.state.mobileNumbers.length + this.state.emails.length === 1)
        return { text: 'Login', isDisabled: false };
      else if (
        !this.isValidPhone(this.loginForm.get('phone')?.value) &&
        !this.isValidEmail(this.loginForm.get('email')?.value) &&
        this.state.mobileNumbers.length + this.state.emails.length > 1
      )
        return { text: 'Enter phone number', isDisabled: true };
      else return { text: 'Login', isDisabled: false };
    }
    return { text: 'Login', isDisabled: false };
  }

  // resend otp
  resendOTP() {
    this.state.otp = '';
    this.state.otpError = '';
    this.state.otpRequestId = '';
    this.otpSentTo = '';
    this.onSubmit();
  }

  goBack() {
    this.state = { ...initialState };
    this.loginForm.reset();
  }
  nextSecurityStep() {
    if (this.securityStep === 1) {
      const fcControl = this.securityForm.get('fcSecurityForm');
      const nameControl = this.securityForm.get('nameAsPerAP');

      if (fcControl?.invalid || nameControl?.invalid) {
        fcControl?.markAsTouched();
        nameControl?.markAsTouched();
        return;
      }
      const payload = {
        name: this.securityForm.get('nameAsPerAP')?.value,
        fc: this.securityForm.get('fcSecurityForm')?.value,
        step: 1,
      };

      this.isLoading = true;

      this.authService.findMember(payload).subscribe({
        next: (res: any) => {
          if (res?.data?.stateToken) {
            sessionStorage.setItem('state_token', res.data.stateToken);
          }
          this.securityStep++;
          this.isLoading = false;
        },
        error: (err: any) => {
          this.toastr.error(err?.message || 'Something went wrong');
          this.isLoading = false;
        },
      });
    }

    if (this.securityStep === 2) {
      const ritwikControl = this.securityForm.get('ritwikName');

      if (ritwikControl?.invalid) {
        ritwikControl?.markAsTouched();
        return;
      }

      const payload = {
        ritwikName: ritwikControl?.value,
        step: 2,
      };

      this.isLoading = true;

      this.authService.findMember(payload).subscribe({
        next: (res: any) => {
          if (res?.data?.stateToken) {
            sessionStorage.setItem('state_token', res.data.stateToken);
          }
          this.securityStep++;
          this.isLoading = false;
        },
        error: (err: any) => {
          this.toastr.error(err?.message || 'Something went wrong');
          this.isLoading = false;
        },
      });
    }

    if (this.securityStep === 3) {
      const arghyaVal = this.securityForm.get('arghyaSerialNo')?.value?.trim();
      const dpVal = this.securityForm.get('dpSerialNo')?.value?.trim();

      if (!arghyaVal && !dpVal) {
        this.toastr.error(
          'Please enter either Arghya Praswasti Serial No or Dikshya Patra Serial No',
        );
        this.securityForm.get('arghyaSerialNo')?.markAsTouched();
        this.securityForm.get('dpSerialNo')?.markAsTouched();
        return;
      }

      const payload: any = { step: 3 };
      if (arghyaVal) payload.apSerialNo = arghyaVal;
      if (dpVal) payload.serialNo = Number(dpVal);

      this.isLoading = true;

      this.authService.findMember(payload).subscribe({
        next: (res: any) => {
          console.log('token', res?.data?.stateToken);
          if (res?.data?.stateToken) {
            sessionStorage.setItem('state_token', res.data.stateToken);
          }
          this.isIdentityPresentInSec = res.data.isIdentityPresent;
          this.securityStep++;
          this.isLoading = false;
        },
        error: (err: any) => {
          this.toastr.error(err?.message || 'Something went wrong');
          this.isLoading = false;
        },
      });
    }

    if (this.securityStep === 4) {
      const identityNo = this.securityForm.get('identityNo')?.value?.trim();
      const recentApSerialNo = this.securityForm.get('recentApSerialNo')?.value?.trim();
      const recentApAmount = this.securityForm.get('recentApAmount')?.value?.trim();

      let payload = {};
      if (this.isIdentityPresentInSec) {
        if (!identityNo) {
          this.securityForm.get('identityNo')?.markAsTouched();
          this.toastr.error('Please enter Identity Number');
          return;
        } else {
          payload = {
            step: 4,
            identityNo: identityNo,
          };
        }
      } else {
        if (!recentApSerialNo) {
          this.securityForm.get('recentApSerialNo')?.markAsTouched();
          this.toastr.error('Please enter recent Arghya Praswasti Number');
          return;
        }
        if (!recentApAmount) {
          this.securityForm.get('recentApAmount')?.markAsTouched();
          this.toastr.error('Please enter recent Arghya Praswasti Amount');
          return;
        }

        payload = {
          step: 4,
          recentApSerialNo: recentApSerialNo,
          recentIvAmount: Number(recentApAmount),
        };
      }

      this.isLoading = true;
      this.authService.findMember(payload).subscribe({
        next: (res: any) => {
          if (res?.data?.stateToken) {
            sessionStorage.setItem('state_token', res.data.stateToken);
          }

          const appId = sessionStorage.getItem('sso_appId');
          const codeChallenge = sessionStorage.getItem('sso_codeChallenge');

          const sessionPayload: any = {};
          if (appId && appId !== 'null' && appId !== 'undefined')
            sessionPayload.appId = Number(appId);
          if (codeChallenge && codeChallenge !== 'null' && codeChallenge !== 'undefined')
            sessionPayload.codeChallenge = codeChallenge;
          this.authService.createSessionFromSec(sessionPayload).subscribe({
            next: (sessionRes) => {
              console.log('Session created successfully:', sessionRes);
              if (sessionRes.data?.redirectUrl && sessionRes.data?.accessCode) {
                sessionStorage.removeItem('sso_appId');
                sessionStorage.removeItem('sso_codeChallenge');
                sessionStorage.removeItem('state_token');
                localStorage.setItem('sso-session', sessionRes.data['sso-session']);
                let redirectUrl = ``;
                if (this.returnUrl && !environment.production) {
                  redirectUrl = `${this.returnUrl}?accessCode=${sessionRes.data.accessCode}`;
                } else {
                  // redirectUrl = `${sessionRes.data.redirectUrl}?accessCode=${sessionRes.data.accessCode}`;

                  if (this.appMode === 'true') {
                    redirectUrl = `myapp://sso/callback?accessCode=${res.data.accessCode}`;
                  } else {
                    redirectUrl = `${sessionRes.data.redirectUrl}?accessCode=${res.data.accessCode}`;
                  }
                }
                window.location.href = redirectUrl;
              } else {
                localStorage.setItem('sso-session', sessionRes.data['sso-session']);
                this.toastr.success(sessionRes.message || 'Logged in successfully');

                this.router.navigate(['/dashboard']);
              }
            },
            error: (err) => {
              console.error('Error creating session:', err);
              this.isLoading = false;
              this.toastr.error(err?.message || 'Failed to create session');
            },
          });

          this.isLoading = false;
          // this.toastr.success('Identity verified successfully');
        },
        error: (err: any) => {
          this.isLoading = false;
          this.toastr.error(err?.message || 'Something went wrong');
        },
      });
    }

    // if (this.securityStep < 4) {
    //   this.securityStep++;
    // }
  }

  // prevSecurityStep() {
  //   if (this.securityStep > 1) {
  //     this.securityStep--;
  //   }
  // }
  onSelectCountry(event: any) {
    const rules: any = environment.loginRule;
    this.accounts = rules[event];
    this.selectedAccount = this.accounts.accounts[0];
    this.selectedContactUs = this.contactUs[event];
    const country = this.countryList.filter((item) => item.id == event && item.id != 'IN');
    this.appCountryName = country.length > 0 ? country[0].title : '';
  }
  cancelSecurityFlow() {
    this.openSecurityForm = false;
    this.securityForm.reset();
    this.securityStep = 1;
    this.state.showOtp = false;
    this.state.otp = '';
    this.state.otpError = '';
    this.state.otpRequestId = '';
    this.otpSentTo = '';
    this.loginForm.get('phone')?.reset();
    this.state.selectedMemId = '';
    this.state.selectedMember = null;
  }

  register() {
    this.router.navigate(['/register'], { queryParamsHandling: 'merge' });
  }

  isNextDisabled(): boolean {
    if (!this.securityForm || this.isLoading) return true;

    switch (this.securityStep) {
      case 1:
        return !!(
          this.securityForm.get('fcSecurityForm')?.invalid ||
          this.securityForm.get('nameAsPerAP')?.invalid
        );
      case 2:
        return !!this.securityForm.get('dpSerialNo')?.invalid;
      case 3:
        const arghyaVal = this.securityForm.get('arghyaSerialNo')?.value?.trim();
        const dpVal = this.securityForm.get('dpSerialNo')?.value?.trim();
        return !(arghyaVal || dpVal);
      case 4:
        return this.securityForm.get('identityNo')?.invalid || this.isLoading;
      default:
        return false;
    }
  }

  onSerialChange(changedField: 'arghyaSerialNo' | 'dpSerialNo') {
    const arghyaControl = this.securityForm.get('arghyaSerialNo');
    const dpControl = this.securityForm.get('dpSerialNo');

    if (changedField === 'arghyaSerialNo' && arghyaControl?.value?.trim()) {
      dpControl?.setValue('');
    } else if (changedField === 'dpSerialNo' && dpControl?.value?.trim()) {
      arghyaControl?.setValue('');
    }
  }

  isValidPhone(phone: string | undefined | null): boolean {
    if (!phone) return false;
    return /^[0-9]{10}$/.test(phone);
  }

  isValidEmail(email: string | undefined | null): boolean {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  maskEmail(email: string): string {
    if (!email || !email.includes('@')) return email;
    const [local, domain] = email.split('@');

    // If too short to safely mask, return minimal mask
    if (local.length <= 6) {
      return local[0] + '*'.repeat(Math.max(local.length - 2, 1)) + local.at(-1)! + '@' + domain;
    }

    const start = local.slice(0, 3);
    const end = local.slice(-3);
    const masked = '*'.repeat(local.length - 6);

    return `${start}${masked}${end}@${domain}`;
  }

  maskContact(contact: string): string {
    // Ensure it's digits only
    if (!/^\d+$/.test(contact)) return contact;

    // Mask 6 middle digits
    return contact.replace(/^(\d{2})\d{6}(\d+)$/, (_, start, end) => {
      return start + '******' + end;
    });
  }

  getCountryByAppId(appId: number) {
    console.log('getCountry API called');
    this.authService.getCountryDetails(appId).subscribe({
      next: (result: any) => {
        if (!result.error) {
          console.log(result);
          this.appCountryName = result.data[0]?.saCountry?.toLowerCase();
          this.appCountryId = this.countryList.find(
            (c) => c.title?.toLowerCase() === this.appCountryName,
          )?.id!;
          if (this.appCountryId?.length) {
            this.selectedCountry.setValue(this.appCountryId);
            this.onSelectCountry(this.appCountryId);
            this.selectedContactUs = this.contactUs[this.selectedCountry.value!];
            console.log(this.selectedContactUs);
          }
        }
        console.log(this.selectedCountry);
      },
      error: (error: any) => {
        this.toastr.error(error);
      },
    });
  }
}
